import { ClientGroup } from 'frontend/client-groups'
import { Cluster } from 'frontend/clusters'
import { IDataModelTypeConfig } from 'frontend/dataModels/types/IDataModelTypeConfig'
import { DriverSource } from 'frontend/driver-sources'
import { Driver } from 'frontend/drivers'
import { Osl } from 'frontend/osls'
import { Route } from 'frontend/routes'
import { VehicleSource } from 'frontend/vehicle-sources'
import { VehicleType } from 'frontend/vehicle-types'
import { Vehicle } from 'frontend/vehicles'
import { markRaw } from 'vue'

export const BusModuleSubsystemDataModelTypeConfig: IDataModelTypeConfig = {
  fields: [
    'id',
    'eid',
    'description',
    'name',
    'updated_at',
    'created_at',
    'allocation_stats',
    { clusters: ['id', 'name'] },
    { bus_routes: ['id', 'name', 'color'] },
    { client_groups: ['id', 'name', 'background_color', 'border_color', 'font_color'] },
    {
      operational_service_levels: [
        'id',
        'name',
        'font_color',
        'background_color',
        'border_color',
        'description',
      ],
    },
    { vehicle_sources: ['id', 'name', 'billable', 'maintainable'] },
    { vehicle_types: ['id', 'name'] },
    { driver_sources: ['id', 'name'] },
    {
      vehicles: [
        'id',
        'fleet_number',
        'registration_plate',
        { cluster: ['id', 'name', 'time_zone'] },
        { vehicle_model: ['id', 'name', { vehicle_brand: ['id', 'name'] }] },
        { vehicle_type: ['id', 'name'] },
      ],
    },
    {
      drivers: [
        'id',
        'name',
        'surname',
        'assigned_phone',
        'personal_phone',
        'primary_phone',
        { cluster: ['id', 'name', 'time_zone'] },
        { driver_category: ['id', 'name', 'background_color', 'border_color', 'font_color'] },
        {
          driver_source: ['id', 'name'],
        },
      ],
    },
  ],
  subscriptions: {
    items: 'BusModuleSubsystemsChannel',
    clusters: 'ClustersSimpleChannel',
    busRoutes: 'BusRoutesFlatChannel',
    clientGroups: 'ClientGroupsSimpleChannel',
    operationalServiceLevels: 'OperationalServiceLevelsSimpleChannel',
    vehicleSources: 'VehicleSourcesSimpleChannel',
    vehicleTypes: 'VehicleTypesSimpleChannel',
    driverSources: 'DriverSourcesSimpleChannel',
    vehicles: 'VehiclesSimpleChannel',
    drivers: 'DriversSimpleChannel',
  },
  defaultColumns: [
    'eid',
    'name',
    'clusters',
    'busRoutes',
    'clientGroups',
    'osls',
    'vehicleSources',
    'vehicleTypes',
    'driverSources',
    'vehicles',
    'drivers',
    'description',
  ],
  columnConfig: {
    eid: {
      label: 'EID',
      header: {
        sort: 'eid',
      },
      cell: {
        kind: 'simple',
        path: 'eid',
      },
    },
    name: {
      label: 'Name',
      header: {
        sort: 'name',
      },
      cell: {
        kind: 'simple',
        path: 'name',
      },
    },
    description: {
      label: 'Description',
      header: {
        sort: 'description',
      },
      cell: {
        kind: 'simple',
        path: 'description',
      },
    },
    osls: {
      label: 'OSLs',
      tooltip: 'Operational service levels',
      header: {},
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: el => ({
          items: el.operationalServiceLevels,
          limit: 5,
          component: markRaw(Osl),
          componentProps: ell => ({ osl: ell }),
        }),
      },
    },
    clusters: {
      label: 'Clusters',
      header: {
        sort: 'clusterName',
      },
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.clusters,
          limit: 3,
          component: Cluster,
          componentProps: cluster => ({ cluster }),
        }),
      },
    },
    busRoutes: {
      label: 'Bus routes',
      header: {
        sort: 'routeName',
      },
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.busRoutes,
          limit: 3,
          component: Route,
          componentProps: route => ({ route }),
        }),
      },
    },
    clientGroups: {
      label: 'Client Groups',
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.clientGroups,
          limit: 3,
          component: ClientGroup,
          componentProps: cg => ({ clientGroup: cg }),
        }),
      },
    },
    vehicleTypes: {
      label: 'Vehicle types',
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.vehicleTypes,
          limit: 3,
          component: VehicleType,
          componentProps: vt => ({ vehicleType: vt }),
        }),
      },
    },
    driverSources: {
      label: 'Driver Sources',
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.driverSources,
          limit: 3,
          component: DriverSource,
          componentProps: ds => ({ driverSource: ds }),
        }),
      },
    },
    vehicleSources: {
      label: 'Vehicle Sources',
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.vehicleSources,
          limit: 3,
          component: VehicleSource,
          componentProps: vs => ({ vehicleSource: vs }),
        }),
      },
    },
    vehicles: {
      label: 'Assigned Vehicles',
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.vehicles,
          limit: 3,
          component: Vehicle,
          componentProps: vehicle => ({ vehicle: vehicle }),
        }),
      },
    },
    drivers: {
      label: 'Assigned Drivers',
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: busModuleSubsystem => ({
          items: busModuleSubsystem.drivers,
          limit: 3,
          component: Driver,
          componentProps: driver => ({ driver: driver }),
        }),
      },
    },
    createdDatetime: {
      label: 'Created datetime',
      header: {
        sort: 'createdAt',
      },
      cell: {
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({ t: item.createdAt }),
      },
    },
    createdTime: {
      label: 'Created time',
      header: {
        sort: 'createdAt',
      },
      cell: {
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({
          t: item.createdAt,
          format: 'timeOnly',
        }),
      },
    },
    createdDate: {
      label: 'Created date',
      header: {
        sort: 'createdAt',
      },
      cell: {
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({
          t: item.createdAt,
          format: 'dateOnly',
        }),
      },
    },
    updatedDatetime: {
      label: 'Updated datetime',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({ t: item.updatedAt }),
      },
    },
    updatedTime: {
      label: 'Updated time',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({
          t: item.updatedAt,
          format: 'timeOnly',
        }),
      },
    },
    updatedDate: {
      label: 'Updated date',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({
          t: item.updatedAt,
          format: 'dateOnly',
        }),
      },
    },
  },
}
